/**
 * 复习会话的显式状态机。全部相变都在这一个纯模块里，域内无 IO，屏幕不各自维护 phase。
 * reduceSession 是全函数，阶段不匹配的事件原样返回 state 且不交出提交，所以双击与重放都不会写两次。
 * 只有 commit 会记账且只把 SubmitInput 交出去，preview 轮的相变照跑但不记账。
 * 用 model、plan 与 grading，由 workspace 驱动，不选卡，也不写日志。
 */

import type { Issue } from '../model/result'
import type { CardRef, PlanId } from '../model/ids'
import { submissionId } from '../model/ids'
import type { GradeOutcome } from './question-types'
import type { AnyQuestion, QuestionAnswer } from '../model/question'
import { gradeAnswer } from './grading'
import type { Rating } from './scheduling'
import type { Plan, PlanIntent, QueueItem } from './plan'
import { cardOf, itemScoring } from './plan'
import type { SubmitInput, SubmitResult } from '../workspace'

export interface Session {
	readonly planId: PlanId
	readonly intent: PlanIntent
	readonly items: readonly QueueItem[]
	/** false 表示这一轮只读：commit 事件照常推进，但永远不交出 SubmitInput。 */
	readonly committable: boolean
}

/** 进行中的几种阶段共享同一组字段，只有 phase 与 pending 不同，所以相变就是改 phase。 */
interface Running {
	readonly session: Session
	readonly cursor: number
	/** 本次会话已交出的卡，只服务汇总页显示。调度一律读日志，两者不一致时日志为准。 */
	readonly committed: readonly CardRef[]
}

/**
 * 已作答、尚未记账的那一步。
 * auto 带答案与判分结果，self 只带档位，和 SubmitInput 的两个分支一一对应，
 * 所以「客观题被手工打分」这类混用在这一层就构造不出来。
 */
export type Pending =
	| { readonly kind: 'auto'; readonly answer: QuestionAnswer; readonly outcome: GradeOutcome }
	| { readonly kind: 'self'; readonly rating: Rating }

/**
 * 记账的三种结局，视图照着渲染，不需要自己判断「这次到底写没写进历史」。
 * readOnly = 只读轮什么都没写；rejected = 卡片已从仓库消失或提交形态被拒，附诊断。
 */
export type Receipt =
	| { readonly kind: 'recorded'; readonly result: SubmitResult }
	| { readonly kind: 'rejected'; readonly issues: readonly Issue[] }
	| { readonly kind: 'readOnly' }

export type SessionState =
	| { readonly phase: 'idle' }
	/** 客观题等交卷（respond）；自评题等用户点「展开参考答案」（reveal）。 */
	| (Running & { readonly phase: 'answering' })
	/** 自评题专用：复述要点与参考答案已展开，等用户报告档位（rate）。 */
	| (Running & { readonly phase: 'reporting' })
	/** 结论已在手上，等 commit。 */
	| (Running & { readonly phase: 'revealed'; readonly pending: Pending })
	/** SubmitInput 已交出，等外部把结局送回来（recorded）。这一步让重复的 commit 事件无害。 */
	| (Running & { readonly phase: 'submitting'; readonly pending: Pending })
	| (Running & { readonly phase: 'committed'; readonly receipt: Receipt })
	| {
			readonly phase: 'ended'
			readonly session: Session
			readonly reason: 'complete' | 'abandoned'
			readonly committed: readonly CardRef[]
		}

export type SessionEvent =
	| { readonly t: 'start'; readonly plan: Plan }
	| { readonly t: 'respond'; readonly answer: QuestionAnswer }
	| { readonly t: 'reveal' }
	| { readonly t: 'rate'; readonly rating: Rating }
	| { readonly t: 'commit' }
	| { readonly t: 'recorded'; readonly receipt: Receipt }
	| { readonly t: 'next' }
	| { readonly t: 'abandon' }

/** 状态机唯一的对外副作用。它存在的理由是让 reducer 保持纯。 */
export type Commit = { readonly t: 'none' } | { readonly t: 'submit'; readonly input: SubmitInput }

export type Step = { readonly state: SessionState; readonly commit: Commit }

export const IDLE: SessionState = { phase: 'idle' }

/**
 * 当前这道题的唯一入口。`state.session.items[state.cursor]` 只在进行中阶段合法，
 * 每个视图自己重复这套 narrowing 会写四遍。
 */
export function currentOf(state: SessionState): QueueItem | null {
	switch (state.phase) {
		case 'idle':
		case 'ended':
			return null
		default:
			return state.session.items[state.cursor] ?? null
	}
}

const NONE: Commit = { t: 'none' }

/** 进行中阶段的公共字段。相变就是换掉 phase 和它多带的那一个字段，其余原样搬过去。 */
function runningOf(state: Running): Running {
	return { session: state.session, cursor: state.cursor, committed: state.committed }
}

function endedOf(state: { session: Session; committed: readonly CardRef[] }, reason: 'complete' | 'abandoned'): SessionState {
	return { phase: 'ended', session: state.session, reason, committed: state.committed }
}

/** scoring 为 auto 的队列项一定是题卡：知识点卡恒为 self。这里只是把那句话翻译成 TS 能看到的 narrowing。 */
function questionOf(item: QueueItem): AnyQuestion | null {
	return item.subject.kind === 'question' ? item.subject.question : null
}

/** 幂等键绑在 (planId, cursor) 上，所以崩溃后重放同一次作答得到同一个 submissionId。 */
function submitInputOf(session: Session, cursor: number, item: QueueItem, pending: Pending): SubmitInput {
	const id = submissionId(session.planId, cursor)
	const card = cardOf(item)
	return pending.kind === 'auto'
		? { kind: 'auto', card, answer: pending.answer, submissionId: id }
		: { kind: 'self', card, rating: pending.rating, submissionId: id }
}

/**
 * 全代码库唯一的相变表：
 * idle --start--> answering，空队列则 --start--> ended
 * answering --respond-->（scoring auto）revealed；answering --reveal-->（scoring self）reporting
 * reporting --rate--> revealed
 * revealed --commit--> submitting（可记账轮）或 committed + receipt{readOnly}（只读轮）
 * submitting --recorded--> committed，receipt 是 recorded 或 rejected
 * rejected 之后照常 --next-->，因为「这张卡不见了」不该把这一轮卡死
 * committed --next--> answering 或 ended
 * 任意进行中阶段 --abandon--> ended
 * 其余组合原样返回，所以每个分支都不需要「先判断能不能点」。
 */
export function reduceSession(state: SessionState, event: SessionEvent): Step {
	switch (event.t) {
		case 'start': {
			if (state.phase !== 'idle') return { state, commit: NONE }
			const session: Session = {
				planId: event.plan.planId,
				intent: event.plan.intent,
				items: event.plan.items,
				// 只读与可记账的唯一分界点，挡在交出 SubmitInput 之前，所以 preview 没有第二条记账路径。
				committable: event.plan.intent.kind !== 'preview',
			}
			if (session.items.length === 0) return { state: endedOf({ session, committed: [] }, 'complete'), commit: NONE }
			return { state: { phase: 'answering', session, cursor: 0, committed: [] }, commit: NONE }
		}

		case 'reveal': {
			if (state.phase !== 'answering') return { state, commit: NONE }
			const item = currentOf(state)
			if (item === null || itemScoring(item) !== 'self') return { state, commit: NONE }
			return { state: { phase: 'reporting', ...runningOf(state) }, commit: NONE }
		}

		case 'respond': {
			if (state.phase !== 'answering') return { state, commit: NONE }
			// 只有自动判分的题卡能交卷。自评题的答案进不了 advance，所以这里不放行。
			const item = currentOf(state)
			const question = item === null ? null : questionOf(item)
			if (item === null || question === null || itemScoring(item) !== 'auto') return { state, commit: NONE }
			const pending: Pending = { kind: 'auto', answer: event.answer, outcome: gradeAnswer(question, event.answer) }
			return { state: { phase: 'revealed', ...runningOf(state), pending }, commit: NONE }
		}

		case 'rate': {
			// reporting 只能从自评题的 answering 进来，所以这里不必再问一次「这题是不是自评」。
			if (state.phase !== 'reporting') return { state, commit: NONE }
			const pending: Pending = { kind: 'self', rating: event.rating }
			return { state: { phase: 'revealed', ...runningOf(state), pending }, commit: NONE }
		}

		case 'commit': {
			if (state.phase !== 'revealed') return { state, commit: NONE }
			const item = currentOf(state)
			if (item === null) return { state, commit: NONE }
			if (!state.session.committable) {
				return { state: { phase: 'committed', ...runningOf(state), receipt: { kind: 'readOnly' } }, commit: NONE }
			}
			const input = submitInputOf(state.session, state.cursor, item, state.pending)
			return { state: { phase: 'submitting', ...runningOf(state), pending: state.pending }, commit: { t: 'submit', input } }
		}

		case 'recorded': {
			// 只有 submitting 接受结局。重复的 recorded（重试、崩溃重放）原样返回，所以 committed 不会被写第二次。
			if (state.phase !== 'submitting') return { state, commit: NONE }
			const item = currentOf(state)
			const recorded = event.receipt.kind === 'recorded' && item !== null ? [...state.committed, cardOf(item)] : state.committed
			return { state: { phase: 'committed', ...runningOf(state), committed: recorded, receipt: event.receipt }, commit: NONE }
		}

		case 'next': {
			if (state.phase !== 'committed') return { state, commit: NONE }
			const cursor = state.cursor + 1
			if (cursor >= state.session.items.length) return { state: endedOf(state, 'complete'), commit: NONE }
			return { state: { phase: 'answering', session: state.session, cursor, committed: state.committed }, commit: NONE }
		}

		case 'abandon': {
			if (state.phase === 'idle' || state.phase === 'ended') return { state, commit: NONE }
			return { state: endedOf(state, 'abandoned'), commit: NONE }
		}
	}
}
